import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import deCommon from '@/i18n/local/de/common';
import enCommon from '@/i18n/local/en/common';
import sqCommon from '@/i18n/local/sq/common';
import itCommon from '@/i18n/local/it/common';
import frCommon from '@/i18n/local/fr/common';

const LANGUAGES = [
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'sq', label: 'Shqip', flag: '🇦🇱' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
];

const ALL_DATA: Record<string, Record<string, string | string[]>> = {
  de: deCommon,
  en: enCommon,
  sq: sqCommon,
  it: itCommon,
  fr: frCommon,
};

const GROUP_NAME_KEYS: Record<string, string> = {
  nav: 'admy.grp_nav',
  hero: 'admy.grp_hero',
  services: 'admy.grp_services',
  about: 'admy.grp_about',
  pricing: 'admy.grp_pricing',
  booking: 'admy.grp_booking',
  footer: 'admy.grp_footer',
  gallery: 'admy.grp_gallery',
  team: 'admy.grp_team',
  clinic: 'admy.grp_clinic',
  process: 'admy.grp_process',
  trust: 'admy.grp_trust',
  dental: 'admy.grp_dental',
  overview: 'admy.grp_overview',
  implants: 'admy.grp_implants',
  contact: 'admy.grp_contact',
  seo: 'admy.grp_seo',
  admin: 'admy.grp_admin',
  privacy: 'admy.grp_privacy',
  imprint: 'admy.grp_imprint',
  agb: 'admy.grp_agb',
  language: 'admy.grp_language',
  service_detail: 'admy.grp_service_detail',
  instagram: 'admy.grp_instagram',
};

type TFunc = (key: string) => string;

function getGroupName(key: string, t: TFunc): string {
  const prefix = key.split('.')[0];
  return GROUP_NAME_KEYS[prefix] ? t(GROUP_NAME_KEYS[prefix]) : prefix;
}

function groupKeys(keys: string[], t: TFunc) {
  const groups: Record<string, string[]> = {};
  keys.forEach((key) => {
    const group = getGroupName(key, t);
    if (!groups[group]) groups[group] = [];
    groups[group].push(key);
  });
  return groups;
}

export default function TranslationsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeGroup, setActiveGroup] = useState<string>('all');

  useEffect(() => {
    if (sessionStorage.getItem('admin_logged_in') !== 'true') {
      navigate('/admin');
    }
  }, [navigate]);

  const allKeys = useMemo(() => {
    const keys = new Set<string>();
    Object.values(ALL_DATA).forEach((data) => {
      Object.keys(data).forEach((key) => keys.add(key));
    });
    return Array.from(keys).sort();
  }, []);

  const groups = useMemo(() => groupKeys(allKeys, t), [allKeys, t]);

  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q && activeGroup === 'all') return allKeys;
    return allKeys.filter((key) => {
      const matchesGroup = activeGroup === 'all' || getGroupName(key, t) === activeGroup;
      const matchesSearch =
        !q ||
        key.toLowerCase().includes(q) ||
        Object.values(ALL_DATA).some((data) => String(data[key] ?? '').toLowerCase().includes(q));
      return matchesGroup && matchesSearch;
    });
  }, [allKeys, searchQuery, activeGroup, t]);

  const groupNames = useMemo(() => ['all', ...Object.keys(groups).sort()], [groups]);

  if (sessionStorage.getItem('admin_logged_in') !== 'true') return null;

  return (
    <div className="px-4 md:px-6 py-6 space-y-6">
      {/* ── Header ─────────────────────────────── */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-xl font-bold text-foreground-900">
            {t('admin.translations.title', 'Sprachen & Übersetzungen')}
          </h1>
          <p className="mt-0.5 text-xs text-foreground-500 font-body">
            {allKeys.length} {t('admin.translations.entries', 'Einträge')} · {t('admin.translations.five_langs', '5 Sprachen')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs font-label px-3 py-1.5 rounded-full bg-accent-100 text-accent-700 whitespace-nowrap">
            <i className="ri-eye-line mr-1"></i>
            {t('adm2.read_only')}
          </span>
        </div>
      </div>

      {/* ── Info Banner ───────────────────────── */}
      <div className="bg-accent-50/50 border border-accent-200/50 rounded-xl px-4 py-3">
        <p className="text-xs text-accent-700 font-body">
          <i className="ri-information-line mr-1"></i>
          {t('adm2.translations_info')}
        </p>
      </div>

      {/* ── Filter + Search ─────────────────────── */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
        {/* Group Chips */}
        <div className="flex items-center gap-2 flex-wrap">
          {groupNames.map((group) => (
            <button
              key={group}
              onClick={() => setActiveGroup(group)}
              className={`px-3 py-1.5 rounded-full text-[10px] font-label tracking-wide transition-all cursor-pointer whitespace-nowrap ${
                activeGroup === group
                  ? 'bg-primary-500 text-white'
                  : 'bg-background-100 text-foreground-500 hover:bg-background-200'
              }`}
            >
              {group === 'all' ? t('admin.translations.all', 'Alle') : group}
            </button>
          ))}
        </div>
        {/* Search */}
        <div className="relative flex-1 w-full sm:max-w-sm sm:ml-auto">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('admin.translations.search', 'Suchen...')}
            className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center rounded-full hover:bg-background-100 text-foreground-400 cursor-pointer"
            >
              <i className="ri-close-line text-xs"></i>
            </button>
          )}
        </div>
      </div>

      {/* ── Entries List ─────────────────────────── */}
      <div className="space-y-3">
        {filtered.length === 0 && (
          <div className="text-center py-16 bg-background-50 rounded-xl border border-background-200/70">
            <i className="ri-translate-2 text-4xl text-foreground-200"></i>
            <p className="mt-3 text-sm text-foreground-400 font-body">
              {t('admin.translations.no_results', 'Keine Übersetzungen gefunden.')}
            </p>
          </div>
        )}

        {filtered.map((key) => (
          <div
            key={key}
            className="bg-background-50 rounded-xl border border-background-200/70 px-5 py-4 hover:border-background-300 transition-all"
          >
            <div className="flex items-center gap-3 mb-3">
              <code className="text-[10px] font-mono text-foreground-400 bg-background-100 px-2 py-1 rounded-md whitespace-nowrap">
                {key}
              </code>
              <span className="px-2 py-0.5 rounded-md text-[10px] font-label bg-background-100 text-foreground-500 whitespace-nowrap">
                {getGroupName(key, t)}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
              {LANGUAGES.map((lang) => {
                const value = ALL_DATA[lang.code][key] || '';
                return (
                  <div
                    key={lang.code}
                    className={`p-2.5 rounded-lg border ${
                      value ? 'border-background-200/50 bg-background-50' : 'border-background-200/30 bg-background-50/50'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-sm">{lang.flag}</span>
                      <span className="text-[10px] font-label text-foreground-500">{lang.label}</span>
                      {value ? (
                        <span className="w-1.5 h-1.5 rounded-full bg-accent-500 ml-auto"></span>
                      ) : (
                        <span className="w-1.5 h-1.5 rounded-full bg-background-300 ml-auto"></span>
                      )}
                    </div>
                    <p className={`text-xs font-body break-words leading-relaxed ${value ? 'text-foreground-700' : 'text-foreground-400 italic'}`}>
                      {value || '—'}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}